import React, { useEffect } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { runInAction } from "mobx";
import { observer, useLocalObservable } from "mobx-react";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { CatalogOptionsStore } from "../../../stores/CatalogOptionsStore";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  CreateOptionBody,
  DISPLAY_TYPE_OPTIONS,
  DisplayType,
  OptionApi,
  UpdateOptionBody,
  getApiErrorMessage,
  isDisplayType,
  validateCreateOptionBody,
  validateUpdateOptionBody,
} from "../../../../../models/CatalogOptions/types";

const DISPLAY_TYPE_LIST: FilterValue[] = DISPLAY_TYPE_OPTIONS.map((o) => ({
  id: o.value,
  value: o.label,
}));

export interface CatalogOptionFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  option?: OptionApi | null;
  store: CatalogOptionsStore;
  onClose: () => void;
  onSuccess: (optionId?: number) => void;
}

const CatalogOptionFormModal: React.FC<CatalogOptionFormModalProps> = ({
  open,
  mode,
  option,
  store,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));
  const { userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const canEdit = userStore.hasPermission(EmployeeRoles.EDIT_CATALOG);
  const colors = theme.palette.customColors;

  const form = useLocalObservable(() => ({
    saving: false,
    name: "",
    slug: "",
    display_type: "dropdown" as DisplayType,
    position: "0",
    is_active: true,
    resetForCreate() {
      this.name = "";
      this.slug = "";
      this.display_type = "dropdown";
      this.position = "0";
      this.is_active = true;
    },
    hydrate(opt: OptionApi) {
      this.name = opt.name ?? "";
      this.slug = opt.slug ?? "";
      this.display_type = isDisplayType(String(opt.display_type))
        ? (opt.display_type as DisplayType)
        : "dropdown";
      this.position = String(opt.position ?? 0);
      this.is_active = Boolean(opt.is_active);
    },
  }));

  useEffect(() => {
    if (!open) return;
    runInAction(() => {
      if (mode === "edit" && option) form.hydrate(option);
      else form.resetForCreate();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, option?.id]);

  const handleClose = () => {
    if (form.saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!canEdit) {
      showError(
        "Permission denied",
        "You don't have permission to edit catalog options.",
      );
      return;
    }

    const positionNum = parseInt(form.position, 10);
    const position = Number.isNaN(positionNum) ? 0 : positionNum;

    try {
      form.saving = true;

      if (mode === "create") {
        const body: CreateOptionBody = {
          name: form.name.trim(),
          display_type: form.display_type,
          position,
          is_active: form.is_active,
        };
        const slug = form.slug.trim();
        if (slug) body.slug = slug;

        const validationError = validateCreateOptionBody(body);
        if (validationError) {
          showError("Validation", validationError);
          return;
        }

        const created = await store.createOption(body);
        showSuccess("Option created successfully.");
        onSuccess(created.id);
        onClose();
      } else if (option) {
        const body: UpdateOptionBody = {
          name: form.name.trim(),
          slug: form.slug.trim() || undefined,
          display_type: form.display_type,
          position,
          is_active: form.is_active,
        };
        const validationError = validateUpdateOptionBody(body);
        if (validationError) {
          showError("Validation", validationError);
          return;
        }

        await store.updateOption(option.id, body);
        onSuccess(option.id);
        onClose();
      }
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Failed to save option."));
    } finally {
      form.saving = false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreenDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreenDialog ? 0 : 2,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Typography variant="h6" fontWeight={700} fontSize="1.05rem">
          {mode === "create" ? "Create Option" : "Edit Option"}
        </Typography>
        <IconButton onClick={handleClose} size="small" disabled={form.saving}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        <Grid2 container spacing={2} columns={12}>
          <Grid2 size={{ xs: 12 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Name *
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="Enter option name"
              value={form.name}
              onChange={(e) => {
                form.name = e.target.value || "";
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Slug (optional)
            </CustomTypography>
            <CustomTextField
              width="100%"
              placeholder="Auto-generated from name if empty"
              value={form.slug}
              onChange={(e) => {
                form.slug = e.target.value || "";
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <AppSearchDropdownMulti
              title="Display type"
              isMultiSelect={false}
              local
              list={DISPLAY_TYPE_LIST}
              value={DISPLAY_TYPE_LIST.filter(
                (o) => o.id === form.display_type,
              )}
              borderColor={colors?.blackBorder}
              borderRadius="8px"
              handleSelect={(items) => {
                const next = items[0]?.id;
                if (next && isDisplayType(next)) {
                  form.display_type = next;
                }
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <CustomTypography
              variant="body3"
              color={colors?.greyTextColor}
              sx={{ mb: 0.75 }}
            >
              Position
            </CustomTypography>
            <CustomTextField
              width="100%"
              type="number"
              allowNegativeValue={false}
              allowDecimalValue={false}
              placeholder="0"
              value={form.position}
              onChange={(e) => {
                form.position = e.target.value || "0";
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch
                checked={form.is_active}
                onChange={(_, checked) => {
                  form.is_active = checked;
                }}
                size="small"
              />
              <Typography variant="body2">
                {form.is_active ? "Active" : "Inactive"}
              </Typography>
            </Box>
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Grid2 container spacing={1} columns={12} justifyContent="flex-end">
          <Grid2 size="auto">
            <AppButton
              variant="transparentPrimary"
              label="Cancel"
              onClick={handleClose}
              disabled={form.saving}
            />
          </Grid2>
          <Grid2 size="auto">
            <AppButton
              variant="primary"
              label={
                form.saving ? "Saving…" : mode === "create" ? "Create" : "Save"
              }
              onClick={() => void handleSubmit()}
              disabled={form.saving || !canEdit}
            />
          </Grid2>
        </Grid2>
      </DialogActions>
    </Dialog>
  );
};

export default observer(CatalogOptionFormModal);
